import { usePage } from '@inertiajs/react';
import React, { createContext, ReactNode, useContext, useEffect, useState } from 'react';

interface TrackingConfig {
    enabled: boolean;
    debug?: boolean;
    consent?: ConsentStatus;
    services?: TrackingServices;
    privacy?: PrivacySettings;
    cookieConsent?: CookieConsentConfig;
}

interface ConsentStatus {
    given: boolean;
    categories: {
        necessary: boolean;
        analytics: boolean;
        marketing: boolean;
        functional: boolean;
    };
    timestamp?: string;
}

interface TrackingServices {
    googleTagManager?: {
        enabled: boolean;
        containerId: string;
    };
    googleAnalytics?: {
        enabled: boolean;
        measurementId: string;
    };
    googleAds?: {
        enabled: boolean;
        conversionId: string;
    };
    facebookPixel?: {
        enabled: boolean;
        pixelId: string;
    };
    facebookDomainVerification?: string;
}

interface PrivacySettings {
    anonymize_ip: boolean;
    respect_dnt: boolean;
    cookie_expiry_days: number;
}

interface CookieConsentConfig {
    enabled: boolean;
    position: string;
    theme: string;
    categories: Record<string, ConsentCategory>;
}

interface ConsentCategory {
    enabled: boolean;
    readonly: boolean;
    title: string;
    description: string;
    services?: string[];
}

interface TrackingContextType {
    config: TrackingConfig | null;
    dataLayer: any[];
    consent: ConsentStatus | null;
    updateConsent: (categories: Partial<ConsentStatus['categories']>) => void;
    trackEvent: (eventName: string, eventData?: any) => void;
    pushToDataLayer: (data: any) => void;
    isServiceEnabled: (service: string) => boolean;
}

const TrackingContext = createContext<TrackingContextType | undefined>(undefined);

declare global {
    interface Window {
        dataLayer: any[];
        gtag?: (...args: any[]) => void;
        fbq?: (...args: any[]) => void;
    }
}

export const TrackingProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { tracking, dataLayer: initialDataLayer } = usePage().props as any;
    const [config, setConfig] = useState<TrackingConfig | null>(tracking || null);
    const [consent, setConsent] = useState<ConsentStatus | null>(tracking?.consent || null);

    // Initialize dataLayer
    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.dataLayer = window.dataLayer || [];
            if (initialDataLayer) {
                window.dataLayer.push(initialDataLayer);
            }
        }
    }, [initialDataLayer]);

    // Update config when page props change
    useEffect(() => {
        if (tracking) {
            setConfig(tracking);
            setConsent(tracking.consent);
        }
    }, [tracking]);

    const updateConsent = async (categories: Partial<ConsentStatus['categories']>) => {
        const newConsent: ConsentStatus = {
            given: true,
            categories: {
                necessary: true,
                analytics: categories.analytics ?? consent?.categories.analytics ?? false,
                marketing: categories.marketing ?? consent?.categories.marketing ?? false,
                functional: categories.functional ?? consent?.categories.functional ?? false,
            },
            timestamp: new Date().toISOString(),
        };

        // Save consent to backend
        try {
            const response = await fetch('/api/tracking/consent', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
                },
                body: JSON.stringify({ categories: newConsent.categories }),
            });

            if (response.ok) {
                setConsent(newConsent);
                // Reload page to apply new consent settings
                window.location.reload();
            }
        } catch (error) {
            console.error('Failed to save consent:', error);
        }
    };

    const pushToDataLayer = (data: any) => {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push(data);

            if (config?.debug) {
                console.log('[Tracking] Data Layer Push:', data);
            }
        }
    };

    const trackEvent = (eventName: string, eventData?: any) => {
        if (!config?.enabled) return;

        const event = {
            event: eventName,
            ...eventData,
            timestamp: new Date().toISOString(),
        };

        pushToDataLayer(event);

        // Send to Google Analytics if available
        if (window.gtag && config.services?.googleAnalytics?.enabled) {
            window.gtag('event', eventName, eventData);
        }

        // Send to Facebook Pixel if available
        if (window.fbq && config.services?.facebookPixel?.enabled) {
            if (eventName === 'PageView') {
                window.fbq('track', 'PageView', eventData);
            } else {
                window.fbq('trackCustom', eventName, eventData);
            }
        }

        if (config.debug) {
            console.log(`[Tracking] Event: ${eventName}`, eventData);
        }
    };

    const isServiceEnabled = (service: string): boolean => {
        if (!config?.enabled) return false;

        const services = config.services as any;
        if (!services) return false;

        switch (service) {
            case 'googleTagManager':
                return services.googleTagManager?.enabled ?? false;
            case 'googleAnalytics':
                return services.googleAnalytics?.enabled ?? false;
            case 'googleAds':
                return services.googleAds?.enabled ?? false;
            case 'facebookPixel':
                return services.facebookPixel?.enabled ?? false;
            default:
                return false;
        }
    };

    return (
        <TrackingContext.Provider
            value={{
                config,
                // SSR (phase 15): the provider renders in node too, where there is no window.
                dataLayer: typeof window !== 'undefined' ? window.dataLayer || [] : [],
                consent,
                updateConsent,
                trackEvent,
                pushToDataLayer,
                isServiceEnabled,
            }}
        >
            {children}
        </TrackingContext.Provider>
    );
};

export const useTracking = () => {
    const context = useContext(TrackingContext);
    if (context === undefined) {
        throw new Error('useTracking must be used within a TrackingProvider');
    }
    return context;
};
